import { Fetcher } from '@this/src/util';

export type TicketCategory = 'changeable' | 'unchangeable';
export type PreferredTransportType = 'rail' | 'air';
export type ProposalBundleState = 'suggesting' | 'selected' | 'rejected';

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  changeable: '変更可',
  unchangeable: '変更不可'
} as const;

export const getTicketCategoryLabel = (category: TicketCategory): string => {
  return TICKET_CATEGORY_LABELS[category];
};

export const PROPOSAL_BUNDLE_STATE = {
  SUGGESTING: 'suggesting',
  SELECTED: 'selected',
  REJECTED: 'rejected'
} as const;

export type ProposalItem = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
  flightName: string | null;
  isConnectionFlight: boolean;
  connectionSummary: string | null;
  seatClass: string | null;
  ticketType: string | null;
  ticketCategory: TicketCategory | null;
  price: number | null;
  priceWithTax: number | null;
  priceText: string | null;
  aiHighlightTags: string[];
  isAir: boolean | null;
  isShinkansen: boolean | null;
  selected: boolean;
};

export type ProposalBundle = {
  id: number;
  category: 'transport' | 'hotel' | 'package' | 'outward' | 'homeward';
  state: ProposalBundleState;
  origin: string | null;
  destination: string | null;
  departureDate: string | null;
  preferredTransportType: PreferredTransportType | null;
  items: ProposalItem[];
};

type ProposalItemResponse = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departure_time: string | null;
  arrival_time: string | null;
  flight_name: string | null;
  is_connection_flight: boolean;
  connection_summary: string | null;
  seat_class: string | null;
  ticket_type: string | null;
  ticket_category: TicketCategory | null;
  price: number | null;
  price_with_tax: number | null;
  price_text: string | null;
  ai_highlight_tags: string[];
  is_air: boolean | null;
  is_shinkansen: boolean | null;
  selected: boolean;
};

type ProposalBundleResponse = {
  id: number;
  category: 'transport' | 'hotel' | 'package' | 'outward' | 'homeward';
  state: 'suggesting' | 'selected' | 'rejected';
  origin: string | null;
  destination: string | null;
  departure_date: string | null;
  preferred_transport_type: PreferredTransportType | null;
  items: ProposalItemResponse[];
};

type FetchProposalBundlesResponse = {
  bundles: ProposalBundleResponse[];
};

const toProposalItem = (item: ProposalItemResponse): ProposalItem => ({
  id: item.id,
  title: item.title,
  subtitle: item.subtitle,
  departureTime: item.departure_time,
  arrivalTime: item.arrival_time,
  flightName: item.flight_name,
  isConnectionFlight: item.is_connection_flight,
  connectionSummary: item.connection_summary,
  seatClass: item.seat_class,
  ticketType: item.ticket_type,
  ticketCategory: item.ticket_category,
  price: item.price,
  priceWithTax: item.price_with_tax,
  priceText: item.price_text,
  aiHighlightTags: item.ai_highlight_tags,
  isAir: item.is_air,
  isShinkansen: item.is_shinkansen,
  selected: item.selected
});

const toProposalBundle = (bundle: ProposalBundleResponse): ProposalBundle => ({
  id: bundle.id,
  category: bundle.category,
  state: bundle.state,
  origin: bundle.origin,
  destination: bundle.destination,
  departureDate: bundle.departure_date,
  preferredTransportType: bundle.preferred_transport_type,
  items: bundle.items.map(toProposalItem)
});

export const fetchProposalBundles = async (tripId: string): Promise<ProposalBundle[]> => {
  const response = await Fetcher.get<FetchProposalBundlesResponse>(`/trips/${tripId}/ai_proposal_bundles.json`);
  return response.bundles.map(toProposalBundle);
};

type RejectProposalBundleResponse = {
  success: boolean;
  bundle_id: number;
  state: string;
};

export const rejectProposalBundle = async (tripId: string, bundleId: number): Promise<{ success: boolean }> => {
  const response = await Fetcher.patch<RejectProposalBundleResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/reject.json`
  );
  return { success: response.success };
};

type SelectProposalItemResponse = {
  success: boolean;
  bundle_id: number;
  item_id: number;
  state: 'suggesting' | 'selected' | 'rejected';
};

export const selectProposalItem = async (
  tripId: string,
  bundleId: number,
  itemId: number
): Promise<{ success: boolean; state: ProposalBundle['state'] }> => {
  const response = await Fetcher.patch<SelectProposalItemResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/items/${itemId}/select.json`
  );
  return { success: response.success, state: response.state };
};

type SummaryItemResponse = {
  category: 'outward' | 'homeward' | 'hotel';
  label: string;
  text: string;
  price: number | null;
  price_with_tax: number | null;
};

type SummaryResponse = {
  items: SummaryItemResponse[];
  total_price: number;
  total_price_with_tax: number;
  has_selection: boolean;
};

type CompleteProposalSelectionResponse = {
  success: boolean;
  message_id: number | null;
  summary: SummaryResponse | null;
  has_selection: boolean;
  already_completed: boolean;
};

export const completeProposalSelection = async (
  tripId: string
): Promise<{ success: boolean; alreadyCompleted: boolean }> => {
  const response = await Fetcher.post<CompleteProposalSelectionResponse>(
    `/trips/${tripId}/ai_proposal_bundles/complete.json`,
    {}
  );
  return { success: response.success, alreadyCompleted: response.already_completed };
};

export type HotelDetail = {
  name: string;
  address: string;
  images: string[];
  latitude: number;
  longitude: number;
  distance: number;
  nearestStationName: string | null;
  stationDistance: number;
  checkin: string;
  checkout: string;
  tel: string;
  cancelPolicy: string;
  refundable: boolean | null;
  propertyAmenities: string[] | null;
  roomAmenities: string[] | null;
  note: string | null;
  parkingInformation: string | null;
  checkinInstructions: string | null;
  specialCheckinInstructions: string | null;
};

export type DestLocation = {
  lat: number;
  lng: number;
};

export type HotelPlanDetail = {
  roomName: string;
  planName: string | null;
  planDetail: string | null;
  price: number;
  priceWithTax: number;
  priceText: string;
};

export type HotelDetailData = {
  hotel: HotelDetail;
  destLocation: DestLocation | null;
  plan: HotelPlanDetail;
};

type HotelDetailResponse = {
  hotel: {
    name: string;
    address: string;
    images: string[];
    latitude: number;
    longitude: number;
    distance: number;
    nearest_station_name: string | null;
    station_distance: number;
    checkin: string;
    checkout: string;
    tel: string;
    cancel_policy: string;
    refundable: boolean | null;
    property_amenities: string[] | null;
    room_amenities: string[] | null;
    note: string | null;
    parking_information: string | null;
    checkin_instructions: string | null;
    special_checkin_instructions: string | null;
  };
  dest_location: { lat: number; lng: number } | null;
  plan: {
    room_name: string;
    plan_name: string | null;
    plan_detail: string | null;
    price: number;
    price_with_tax: number;
    price_text: string;
  };
};

const toHotelDetailData = (response: HotelDetailResponse): HotelDetailData => ({
  hotel: {
    name: response.hotel.name,
    address: response.hotel.address,
    images: response.hotel.images,
    latitude: response.hotel.latitude,
    longitude: response.hotel.longitude,
    distance: response.hotel.distance,
    nearestStationName: response.hotel.nearest_station_name,
    stationDistance: response.hotel.station_distance,
    checkin: response.hotel.checkin,
    checkout: response.hotel.checkout,
    tel: response.hotel.tel,
    cancelPolicy: response.hotel.cancel_policy,
    refundable: response.hotel.refundable,
    propertyAmenities: response.hotel.property_amenities,
    roomAmenities: response.hotel.room_amenities,
    note: response.hotel.note,
    parkingInformation: response.hotel.parking_information,
    checkinInstructions: response.hotel.checkin_instructions,
    specialCheckinInstructions: response.hotel.special_checkin_instructions
  },
  destLocation: response.dest_location,
  plan: {
    roomName: response.plan.room_name,
    planName: response.plan.plan_name,
    planDetail: response.plan.plan_detail,
    price: response.plan.price,
    priceWithTax: response.plan.price_with_tax,
    priceText: response.plan.price_text
  }
});

export const fetchProposalItemHotelDetail = async (tripId: string, itemId: number): Promise<HotelDetailData> => {
  const response = await Fetcher.get<HotelDetailResponse>(
    `/trips/${tripId}/ai_proposal_items/${itemId}/hotel_detail.json`
  );
  return toHotelDetailData(response);
};
